import { useState } from 'react';
import { chartColors } from '@shared/lib/chart-theme';
import { humanize, sumCounts, type LabelledCount } from '@features/analytics/model/dashboard.types';

/**
 * Part-to-whole across a handful of classes: one horizontal stacked bar.
 *
 * Hand-built rather than charted, because at one row the whole thing is two
 * flex boxes and this way the 2px surface gaps and rounded ends are exact. Each
 * segment is direct-labelled in the legend below, which is also what satisfies
 * the contrast relief rule for the aqua and yellow slots.
 */
export function SubscriptionMix({ data }: { data: LabelledCount[] }) {
  const [hovered, setHovered] = useState<string | null>(null);
  const total = sumCounts(data);

  if (total === 0) {
    return <p className="py-8 text-center text-sm text-slate-400">No subscriptions yet.</p>;
  }

  // Colour follows the entity's position in the list, assigned once — never
  // recomputed from rank, so the mix keeps its colours as counts change.
  const segments = data.map((item, index) => ({
    ...item,
    color: chartColors.series[index % chartColors.series.length] as string,
    percent: (item.count / total) * 100,
  }));

  return (
    <div>
      <div
        className="flex h-3 w-full gap-0.5 overflow-hidden"
        role="img"
        aria-label={segments
          .map((segment) => `${humanize(segment.label)}: ${segment.count}`)
          .join(', ')}
      >
        {segments.map((segment, index) => (
          <div
            key={segment.label}
            onMouseEnter={() => setHovered(segment.label)}
            onMouseLeave={() => setHovered(null)}
            style={{
              width: `${segment.percent}%`,
              backgroundColor: segment.color,
              opacity: hovered && hovered !== segment.label ? 0.4 : 1,
            }}
            className={[
              'h-full transition-opacity',
              index === 0 ? 'rounded-l-full' : '',
              index === segments.length - 1 ? 'rounded-r-full' : '',
            ].join(' ')}
          />
        ))}
      </div>

      <ul className="mt-4 space-y-2">
        {segments.map((segment) => (
          <li
            key={segment.label}
            onMouseEnter={() => setHovered(segment.label)}
            onMouseLeave={() => setHovered(null)}
            className="flex items-center gap-2.5 text-sm"
          >
            <span
              aria-hidden
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: segment.color }}
            />
            <span className="flex-1 truncate text-slate-600">{humanize(segment.label)}</span>
            <span className="font-semibold tabular-nums text-slate-900">{segment.count}</span>
            <span className="w-11 text-right tabular-nums text-slate-400">
              {segment.percent.toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
